/**
 * FormField Component
 *
 * Input wrapper with label and error message.
 */
import { type InputHTMLAttributes, type ReactNode } from 'react';

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helperText?: string;
  children?: ReactNode;
}

export const FormField = ({
  label,
  error,
  helperText,
  children,
  id,
  ...inputProps
}: FormFieldProps) => {
  const inputId = id || label.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="mb-4">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
        {inputProps.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {children || (
        <input
          id={inputId}
          {...inputProps}
          className={`
            w-full px-3 py-2 text-sm
            border rounded-lg
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${error
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-metrica-green focus:border-metrica-green'
            }
            disabled:bg-gray-100 disabled:cursor-not-allowed
            ${inputProps.className || ''}
          `}
        />
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

export default FormField;
